import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@patient-watch/ui";
import { AdminPasswordGate } from "../components/AdminPasswordGate";
import { AdminLayout } from "../components/AdminLayout";
import { ExportDatePicker } from "../components/ExportDatePicker";
import { useStudyTrackerData } from "../hooks/useStudyTrackerData";
import { formatStudyNumber } from "../utils/studyTrackerHelpers";
import { downloadParticipantExport } from "../utils/downloadParticipantExport";

export const Route = createFileRoute("/export")({
    component: ExportPage,
});

function ExportPage() {
    return (
        <AdminPasswordGate description="Enter the study admin password to download participant data.">
            <ExportContent />
        </AdminPasswordGate>
    );
}

function ExportContent() {
    const { data, isLoading, isError } = useStudyTrackerData();
    const [startDate, setStartDate] = useState("2026-03-01");
    const [endDate, setEndDate] = useState("2026-05-29");
    const [downloadingStudyNumber, setDownloadingStudyNumber] = useState<number | null>(null);
    const [downloadingAll, setDownloadingAll] = useState(false);

    const participants = useMemo(() => {
        if (!data) return [];
        return [...data.allStudyNumbers].sort((a, b) => a - b);
    }, [data]);

    const dateRangeLabel = useMemo(() => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (!start.isValid() || !end.isValid()) return "";
        return `${start.format("D/M/YYYY")} to ${end.format("D/M/YYYY")}`;
    }, [startDate, endDate]);

    const validateDates = (): boolean => {
        if (!startDate || !endDate) {
            toast.error("Please select a start date and end date.");
            return false;
        }

        if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
            toast.error("Please enter valid dates.");
            return false;
        }

        if (dayjs(startDate).isAfter(dayjs(endDate))) {
            toast.error("Start date must be on or before end date.");
            return false;
        }

        return true;
    };

    const handleDownload = async (studyNumber: number) => {
        if (!validateDates()) return;

        setDownloadingStudyNumber(studyNumber);
        try {
            await downloadParticipantExport({
                studyNumber,
                startDate,
                endDate,
            });
            toast.success(`Downloaded participant ${studyNumber}.`);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Download failed.");
        } finally {
            setDownloadingStudyNumber(null);
        }
    };

    const handleDownloadAll = async () => {
        if (!validateDates() || participants.length === 0) return;

        setDownloadingAll(true);
        try {
            for (const studyNumber of participants) {
                await downloadParticipantExport({
                    studyNumber,
                    startDate,
                    endDate,
                });
            }
            toast.success(`Downloaded ${participants.length} participant files.`);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Download failed.");
        } finally {
            setDownloadingAll(false);
        }
    };

    if (isLoading) {
        return (
            <AdminLayout>
                <div className="py-12 text-center">Loading participants...</div>
            </AdminLayout>
        );
    }

    if (isError || !data) {
        return (
            <AdminLayout>
                <Card className="mx-auto max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <h2 className="mb-4 font-bold text-red-600 text-2xl">Error</h2>
                            <p className="text-gray-600">Failed to load participant list.</p>
                        </div>
                    </CardContent>
                </Card>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Date Range</CardTitle>
                        <CardDescription>Choose the diary dates to include in each export file.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
                            <ExportDatePicker
                                id="start-date"
                                label="Start date"
                                value={startDate}
                                onChange={setStartDate}
                            />
                            <ExportDatePicker id="end-date" label="End date" value={endDate} onChange={setEndDate} />
                        </div>
                        {dateRangeLabel ? (
                            <p className="text-slate-600 text-sm">Exporting diary entries from {dateRangeLabel}.</p>
                        ) : null}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row justify-between items-start gap-4">
                        <div>
                            <CardTitle>Participants</CardTitle>
                            <CardDescription>
                                Each file contains 5-minute interval rows for every diary day in the selected range.
                            </CardDescription>
                        </div>
                        <Button
                            variant="secondary"
                            disabled={downloadingAll || participants.length === 0}
                            onClick={handleDownloadAll}
                        >
                            {downloadingAll ? (
                                <>
                                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <Download className="mr-2 w-4 h-4" />
                                    Download All
                                </>
                            )}
                        </Button>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Study Number</TableHead>
                                    <TableHead>Filename</TableHead>
                                    <TableHead className="text-right">Download</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {participants.map((studyNumber) => {
                                    const filename = `SEAGM_${String(studyNumber).padStart(2, "0")}_diary.xlsx`;
                                    const isDownloading = downloadingStudyNumber === studyNumber;

                                    return (
                                        <TableRow key={studyNumber}>
                                            <TableCell className="font-medium">
                                                {formatStudyNumber(studyNumber)}
                                            </TableCell>
                                            <TableCell className="font-mono text-sm">{filename}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    disabled={downloadingAll || isDownloading}
                                                    onClick={() => handleDownload(studyNumber)}
                                                >
                                                    {isDownloading ? (
                                                        <>
                                                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                                                            Downloading
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Download className="mr-2 w-4 h-4" />
                                                            Download
                                                        </>
                                                    )}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
}
